/**
 * The map-asset painter (`projects/map-assets.md` 3.1, `decisions/0193`): one Canvas2D drawing
 * per {@link AssetLook}, cached, in the terrain painter's materials and light.
 *
 * The board is seen as a slice at head height, so a standing tree is its trunk cut through: a scale
 * tree's round cut in lattice-scarred bark, a seal tree's fluted cut in ribbed bark, each on a column
 * whose south face shows, in the same family as the `giant-trunk` tile's cut top. Below the slice
 * the plants show whole: a horsetail thicket's jointed stems with their needle whorls, and a fallen
 * trunk laid in sections. A hauler wreck is a burnt-out tracked cargo hauler lying askew. The colours are the main atlas's bark and foliage (developer, 2026-09-26:
 * familiar colours, so a tree reads as a tree to new players). The light is the terrain's, from the north-west, so shadows fall south-east.
 *
 * The scale and seal trees draw from generated sprites (`treeSprites.ts`, `decisions/0193` point 7 as
 * amended 2026-10-01); their painted drawings here are what a tree shows until those load.
 *
 * **Everything stays inside the footprint** (orchestrator call 7): only a soft shadow spills, and by
 * less than {@link ASSET_MARGIN}. A drawing covers its footprint plus that margin on every side.
 */
import { CELL } from "../combatCells";
import { rnd } from "./geometry";
import { type Ctx2D, type Materials, pattern } from "./materials";
import type { MaterialId } from "./tileArt";
import { type AssetBand, type AssetLook, type TrunkPlace, assetLookKey } from "./assetLook";

/** How far past the footprint a drawing's canvas reaches, for the shadow: half a cell. */
export const ASSET_MARGIN = CELL / 2;

/** A drawing's canvas side in device pixels. */
export function assetCanvasSide(look: AssetLook, resolution: number): number {
  return Math.ceil((look.cells * CELL + 2 * ASSET_MARGIN) * resolution);
}

type Point = [number, number];
type Art = { g: Ctx2D; m: Materials; res: number; seed: number; damaged: boolean };

const SPRITES = new WeakMap<Materials, Map<string, OffscreenCanvas>>();

/** The drawing for `look` at `resolution`, painted once per materials load and then reused. */
export function assetSprite(look: AssetLook, materials: Materials, resolution: number): OffscreenCanvas {
  let cache = SPRITES.get(materials);
  if (!cache) SPRITES.set(materials, (cache = new Map()));
  const key = `${assetLookKey(look)}|${resolution}`;
  let canvas = cache.get(key);
  if (!canvas) {
    const side = assetCanvasSide(look, resolution);
    canvas = new OffscreenCanvas(side, side);
    const g = canvas.getContext("2d")!;
    g.setTransform(resolution, 0, 0, resolution, ASSET_MARGIN * resolution, ASSET_MARGIN * resolution);
    paintAsset(g, look, { materials, resolution });
    cache.set(key, canvas);
  }
  return canvas;
}

/** Paint `look` with its footprint at `0..cells*CELL` in `g`'s user space. */
export function paintAsset(g: Ctx2D, look: AssetLook, { materials, resolution }: { materials: Materials; resolution: number }): void {
  const a: Art = { g, m: materials, res: resolution, seed: look.seed * 7919 + 13, damaged: look.band !== "intact" };
  const side = look.cells * CELL;
  g.save();
  switch (look.assetId) {
    case "scale-tree":
      standingTree(a, side, "lattice");
      break;
    case "seal-tree":
      standingTree(a, side, "ribs");
      break;
    case "horsetail-stand":
      horsetails(a, side);
      break;
    case "fallen-trunk":
      trunkSection(a, look.trunk ?? { role: "single", dir: "e", brokenBefore: false, brokenAfter: false });
      break;
    case "hauler-wreck":
      haulerWreck(a, side, look.band);
      break;
    case "standing-dead":
      standingTree(a, side, "lattice");
      break;
    case "rockfall":
      placeholder(a, side, { fill: "rock-light", round: true });
      break;
    case "smelter":
      placeholder(a, side, { fill: "metal-bulk", round: true, glow: true });
      break;
    case "mech-cradle":
      placeholder(a, side, { fill: "metal-bulk", round: false });
      break;
    case "ore-cart":
      placeholder(a, side, { fill: "metal-dark", round: false });
      break;
    case "reactor":
      placeholder(a, side, { fill: "metal-bulk", round: true, glow: true });
      break;
    case "mech-printer":
    case "mech-printer-cataphract":
    case "mech-printer-everest":
    case "mech-printer-small":
      placeholder(a, side, { fill: "metal-dark", round: false });
      break;
  }
  g.restore();
}

/**
 * The session-4 set's stand-in until its sprites load (`treeSprites.ts`): a plain mass in the
 * asset's material, lit and shadowed like the rest, so the footprint reads while the art fetches.
 * A smelter's crucible glows.
 */
function placeholder(a: Art, side: number, { fill: id, round, glow = false }: { fill: MaterialId; round: boolean; glow?: boolean }): void {
  const { g } = a;
  const inset = side * 0.06;
  const path = new Path2D();
  if (round) path.arc(side / 2, side / 2, side / 2 - inset, 0, Math.PI * 2);
  else path.rect(inset, inset, side - 2 * inset, side - 2 * inset);
  castShadow(a, path, { dx: side * 0.08, dy: side * 0.1, blur: side * 0.06, alpha: 0.6 });
  g.fillStyle = fill(a, id);
  g.fill(path);
  shade(a, path, { x0: 0, y0: 0, x1: side, y1: side, lit: 0.18, dark: 0.45 });
  if (glow) {
    const r = side * 0.2;
    const grad = g.createRadialGradient(side / 2, side / 2, 0, side / 2, side / 2, r);
    grad.addColorStop(0, a.damaged ? "#c0581c" : "#fff2c8");
    grad.addColorStop(0.6, "#ff8a1f");
    grad.addColorStop(1, "rgba(255,90,20,0)");
    g.fillStyle = grad;
    g.beginPath();
    g.arc(side / 2, side / 2, r, 0, Math.PI * 2);
    g.fill();
  }
  g.strokeStyle = "rgba(14,9,5,0.9)";
  g.lineWidth = 1.4;
  g.stroke(path);
}

// ── Shared strokes ──────────────────────────────────────────────────────────────────────────────

const r01 = (a: Art, k: number): number => rnd(a.seed, k, 71);

function fill(a: Art, id: MaterialId, rot = 0): CanvasPattern {
  return pattern(a.g, a.m, { id, ox: a.seed * 17, oy: a.seed * 5, rot });
}

/** A shape's shadow alone, cast south-east: the shape is drawn far off the canvas and only its shadow lands. */
function castShadow(a: Art, path: Path2D, s: { dx: number; dy: number; blur: number; alpha: number }): void {
  const FAR = 100000;
  const { g, res } = a;
  g.save();
  g.shadowColor = `rgba(8,6,4,${s.alpha})`;
  g.shadowBlur = s.blur * res;
  g.shadowOffsetX = (s.dx + FAR) * res;
  g.shadowOffsetY = s.dy * res;
  g.translate(-FAR, 0);
  g.fillStyle = "#000";
  g.fill(path);
  g.restore();
}

/** A lit-to-shaded wash across `path`, north-west to south-east, over the box `x0,y0..x1,y1`. */
function shade(a: Art, path: Path2D, box: { x0: number; y0: number; x1: number; y1: number; lit: number; dark: number }): void {
  const { g } = a;
  const grad = g.createLinearGradient(box.x0, box.y0, box.x1, box.y1);
  grad.addColorStop(0, `rgba(255,246,222,${box.lit})`);
  grad.addColorStop(0.5, "rgba(0,0,0,0)");
  grad.addColorStop(1, `rgba(0,0,0,${box.dark})`);
  g.fillStyle = grad;
  g.fill(path);
}

/** A tapered blade from radius `r0` out to `len` along angle `ang`, bent sideways by `bend`. */
function blade(path: Path2D, { cx, cy, ang, r0, len, w, bend }: { cx: number; cy: number; ang: number; r0: number; len: number; w: number; bend: number }): void {
  const c = Math.cos(ang);
  const s = Math.sin(ang);
  const at = (along: number, across: number): Point => [cx + c * along - s * across, cy + s * along + c * across];
  const mid = (r0 + len) / 2;
  path.moveTo(...at(r0, -w));
  path.quadraticCurveTo(...at(mid, -w * 0.8 + bend), ...at(len, bend * 1.4));
  path.quadraticCurveTo(...at(mid, w * 0.8 + bend), ...at(r0, w));
  path.closePath();
}

/**
 * A strap-leaf tuft: a dark core with long blades radiating from it. `dry` draws it in the
 * dried-foliage material, `gap` leaves a sector bare (a damaged crown) as `[from, width]` radians.
 */
function tuft(a: Art, { x, y, r, n, salt, dry = false, gap }: { x: number; y: number; r: number; n: number; salt: number; dry?: boolean; gap?: [number, number] }): void {
  const { g } = a;
  const core = new Path2D();
  core.arc(x, y, r * 0.42, 0, Math.PI * 2);
  g.fillStyle = fill(a, dry ? "litter" : "bush-dark");
  g.fill(core);
  const blades = new Path2D();
  const rot = rnd(a.seed, salt, 3) * Math.PI * 2;
  for (let k = 0; k < n; k++) {
    const ang = rot + (k / n) * Math.PI * 2 + (rnd(a.seed, salt + k, 5) - 0.5) * 0.35;
    if (gap && inGap(ang, gap)) continue;
    const len = r * (0.72 + 0.28 * rnd(a.seed, salt + k, 7));
    blade(blades, { cx: x, cy: y, ang, r0: r * 0.1, len, w: Math.max(1.1, r * 0.09), bend: (rnd(a.seed, salt + k, 9) - 0.5) * r * 0.22 });
  }
  g.fillStyle = fill(a, dry ? "litter" : "bush");
  g.fill(blades);
  shade(a, blades, { x0: x - r, y0: y - r, x1: x + r, y1: y + r, lit: 0.28, dark: 0.4 });
  g.lineWidth = 0.6;
  g.strokeStyle = dry ? "rgba(44,32,16,0.6)" : "rgba(14,26,8,0.6)";
  g.stroke(blades);
}

function inGap(ang: number, [from, width]: [number, number]): boolean {
  const t = (((ang - from) % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
  return t < width;
}

/** A limb in bark: a dark under-stroke, the bark, and a lit edge. */
function limb(a: Art, path: Path2D, w: number): void {
  const { g } = a;
  g.lineCap = "round";
  g.lineJoin = "round";
  g.strokeStyle = "rgba(16,10,6,0.85)";
  g.lineWidth = w + 2;
  g.stroke(path);
  g.strokeStyle = fill(a, "bark", 90);
  g.lineWidth = w;
  g.stroke(path);
  g.save();
  g.translate(-w * 0.18, -w * 0.18);
  g.strokeStyle = "rgba(255,240,210,0.22)";
  g.lineWidth = w * 0.3;
  g.stroke(path);
  g.restore();
}

/**
 * A lepidodendron's diamond leaf scars: a diagonal lattice with a pale dot in each diamond,
 * clipped to `clip`. `period` divides a cell so the lattice runs on across trunk sections.
 */
function lattice(a: Art, clip: Path2D, { x0, y0, x1, y1, period }: { x0: number; y0: number; x1: number; y1: number; period: number }): void {
  const { g } = a;
  g.save();
  g.clip(clip);
  const lines = new Path2D();
  const h = y1 - y0;
  for (let x = x0 - h; x <= x1 + h; x += period) {
    lines.moveTo(x, y0);
    lines.lineTo(x + h * 0.8, y1);
    lines.moveTo(x, y1);
    lines.lineTo(x + h * 0.8, y0);
  }
  g.strokeStyle = "rgba(24,16,10,0.55)";
  g.lineWidth = 0.9;
  g.stroke(lines);
  g.fillStyle = "rgba(214,192,150,0.35)";
  // A scar at the centre of each diamond: where the half-period-shifted lines of the two families cross.
  const dots = new Path2D();
  const n = Math.ceil((x1 - x0 + 2 * h) / period);
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      const ca = x0 - h + (i + 0.5) * period;
      const cb = x0 - h + (j + 0.5) * period;
      const y = (cb - ca + 0.8 * (y1 + y0)) / 1.6;
      const x = ca + 0.8 * (y - y0);
      if (y <= y0 || y >= y1 || x < x0 || x > x1) continue;
      dots.moveTo(x + 1, y);
      dots.ellipse(x, y, 1, 0.7, 0, 0, Math.PI * 2);
    }
  }
  g.fill(dots);
  g.restore();
}

/** A pale jagged crack across a surface. */
function crack(a: Art, { from, to, salt }: { from: Point; to: Point; salt: number }): void {
  const { g } = a;
  const path = new Path2D();
  path.moveTo(...from);
  const steps = 5;
  for (let k = 1; k <= steps; k++) {
    const t = k / steps;
    const jag = k === steps ? 0 : (rnd(a.seed, salt + k, 11) - 0.5) * 4;
    path.lineTo(from[0] + (to[0] - from[0]) * t + jag, from[1] + (to[1] - from[1]) * t - jag);
  }
  g.lineCap = "round";
  g.strokeStyle = "rgba(12,8,4,0.8)";
  g.lineWidth = 2;
  g.stroke(path);
  g.strokeStyle = "rgba(230,208,166,0.7)";
  g.lineWidth = 0.8;
  g.stroke(path);
}

// ── Standing trees ──────────────────────────────────────────────────────────────────────────────

/**
 * A standing tree's cut: the board is a slice at head height (developer, 2026-09-28: "a bigass tree
 * stump, as if the view of it has been vertically cut off"), so a tree is its trunk's cross-section
 * lifted above the ground on a column, the column's south face showing below it. `ribs` fluted
 * outlines (a seal tree's ribs); 0 is round.
 */
type Column = { c: number; R: number; lift: number; ribs: number; phase: [number, number] };

/** The column's radius at `ang`: a little irregular, and scalloped when it has ribs. */
function columnRadius(col: Column, ang: number): number {
  const wobble = 1 + 0.025 * Math.sin(3 * ang + col.phase[0]) + 0.015 * Math.sin(5 * ang + col.phase[1]);
  const flute = col.ribs ? 1 - 0.045 * (1 - Math.abs(Math.sin((col.ribs * ang) / 2))) : 1;
  return col.R * wobble * flute;
}

const STEPS = 144;

/** The column's outline scaled by `k` about `(cx, cy)`, over the angles `from..to`. */
function outlineAt(col: Column, { cx, cy, k, from = 0, to = Math.PI * 2 }: { cx: number; cy: number; k: number; from?: number; to?: number }): Point[] {
  const n = Math.max(2, Math.ceil((STEPS * Math.abs(to - from)) / (Math.PI * 2)));
  const out: Point[] = [];
  for (let i = 0; i <= n; i++) {
    const ang = from + ((to - from) * i) / n;
    const r = columnRadius(col, ang) * k;
    out.push([cx + Math.cos(ang) * r, cy + Math.sin(ang) * r]);
  }
  return out;
}

function poly(points: Point[], path = new Path2D()): Path2D {
  points.forEach((p, i) => (i === 0 ? path.moveTo(...p) : path.lineTo(...p)));
  path.closePath();
  return path;
}

/** The two ends of the column: the cut on top, the foot on the ground below it. */
const capY = (col: Column): number => col.c - col.lift / 2;
const footY = (col: Column): number => col.c + col.lift / 2;

/**
 * A standing tree: root flares on the ground, the column's shadow and its bark face, and the cut
 * on top (bark ring, pale wood, growth rings, pith). `bark` names the species' bark: a scale tree's
 * diamond leaf-scar lattice, a seal tree's ribs with rows of seal scars. Damaged: deep splits from
 * the pith out through the bark and down the face, bark torn off in chunks, chips on the ground.
 */
function standingTree(a: Art, side: number, bark: "lattice" | "ribs"): void {
  const { g } = a;
  const c = side / 2;
  const lift = side * 0.11;
  const col: Column = { c, R: c - 3 - lift / 2, lift, ribs: bark === "ribs" ? 22 : 0, phase: [r01(a, 1) * 6.28, r01(a, 2) * 6.28] };
  const silhouette = poly([...outlineAt(col, { cx: c, cy: footY(col), k: 1, from: 0, to: Math.PI }), ...outlineAt(col, { cx: c, cy: capY(col), k: 1, from: Math.PI, to: Math.PI * 2 })]);
  const face = poly([...outlineAt(col, { cx: c, cy: footY(col), k: 1, from: 0, to: Math.PI }), ...outlineAt(col, { cx: c, cy: capY(col), k: 1, from: Math.PI, to: 0 })]);
  const cap = poly(outlineAt(col, { cx: c, cy: capY(col), k: 1 }));
  const splits = a.damaged ? [0, 1, 2].map((k) => r01(a, 30 + k) * 0.7 + (k * Math.PI * 2) / 3 + r01(a, 3) * 6.28) : [];
  const torn: [number, number][] = a.damaged ? [0, 1].map((k) => [r01(a, 40 + k) * 0.8 + k * Math.PI + 0.6, 0.55 + r01(a, 42 + k) * 0.4]) : [];

  castShadow(a, silhouette, { dx: side * 0.1, dy: side * 0.12, blur: side * 0.07, alpha: 0.72 });
  rootFlares(a, col, side);
  if (a.damaged) chips(a, { side, col, n: 16 });
  castShadow(a, silhouette, { dx: 1.5, dy: 2, blur: 2, alpha: 0.6 });
  columnFace(a, { col, face, bark, splits, torn });
  cutFace(a, { col, cap, bark, splits, torn });
  g.strokeStyle = "rgba(14,9,5,0.9)";
  g.lineWidth = 1.4;
  g.stroke(silhouette);
}

/** Buttress roots spreading from the column's foot toward the footprint's corners, forking once. */
function rootFlares(a: Art, col: Column, side: number): void {
  const { g } = a;
  const cy = footY(col);
  const inside = (v: number) => Math.min(side - 4, Math.max(4, v));
  const roots = new Path2D();
  const n = 6;
  for (let k = 0; k < n; k++) {
    const ang = Math.PI / 4 + (k * Math.PI * 2) / n + (r01(a, 60 + k) - 0.5) * 0.6;
    const len = col.R * (1.32 + 0.25 * r01(a, 70 + k));
    const w = col.R * (0.2 + 0.08 * r01(a, 75 + k));
    const nx = -Math.sin(ang);
    const ny = Math.cos(ang);
    const tipX = inside(col.c + Math.cos(ang) * len);
    const tipY = inside(cy + Math.sin(ang) * len);
    const mid = col.R * 0.92;
    // A buttress: broad where it leaves the column, narrowing to a rounded toe.
    roots.moveTo(col.c + nx * w * 1.6, cy + ny * w * 1.6);
    roots.quadraticCurveTo(col.c + Math.cos(ang) * mid + nx * w * 0.55, cy + Math.sin(ang) * mid + ny * w * 0.55, tipX + nx * w * 0.45, tipY + ny * w * 0.45);
    roots.quadraticCurveTo(tipX + Math.cos(ang) * w * 0.6, tipY + Math.sin(ang) * w * 0.6, tipX - nx * w * 0.45, tipY - ny * w * 0.45);
    roots.quadraticCurveTo(col.c + Math.cos(ang) * mid - nx * w * 0.55, cy + Math.sin(ang) * mid - ny * w * 0.55, col.c - nx * w * 1.6, cy - ny * w * 1.6);
    roots.closePath();
  }
  castShadow(a, roots, { dx: 2, dy: 3, blur: 3, alpha: 0.55 });
  g.fillStyle = fill(a, "bark-dark", 30);
  g.fill(roots);
  shade(a, roots, { x0: 0, y0: 0, x1: side, y1: side, lit: 0.2, dark: 0.35 });
  g.strokeStyle = "rgba(14,9,5,0.75)";
  g.lineWidth = 1;
  g.stroke(roots);
}

/** Pale wood chips and bark flakes thrown down around a damaged trunk. */
function chips(a: Art, { side, col, n }: { side: number; col: Column; n: number }): void {
  const { g } = a;
  const wood = new Path2D();
  const flakes = new Path2D();
  for (let k = 0; k < n; k++) {
    const ang = r01(a, 100 + k) * Math.PI * 2;
    const d = col.R * (1.02 + 0.3 * r01(a, 130 + k));
    const x = Math.min(side - 4, Math.max(4, col.c + Math.cos(ang) * d));
    const y = Math.min(side - 4, Math.max(4, footY(col) + Math.sin(ang) * d));
    const s = 1.6 + 2.2 * r01(a, 160 + k);
    const rot = r01(a, 190 + k) * Math.PI;
    const pts: Point[] = [0, 1, 2, 3].map((q) => {
      const t = rot + (q * Math.PI) / 2 + (r01(a, 220 + k * 4 + q) - 0.5) * 0.8;
      const r = s * (q % 2 === 0 ? 1 : 0.5);
      return [x + Math.cos(t) * r, y + Math.sin(t) * r];
    });
    poly(pts, k % 3 === 0 ? flakes : wood);
  }
  castShadow(a, wood, { dx: 1, dy: 1.5, blur: 1.5, alpha: 0.6 });
  g.fillStyle = "rgba(214,184,136,0.95)";
  g.fill(wood);
  g.fillStyle = fill(a, "bark-dark");
  g.fill(flakes);
  g.lineWidth = 0.6;
  g.strokeStyle = "rgba(30,20,10,0.8)";
  g.stroke(wood);
}

/** The column's south face in the species' bark, shaded as a cylinder lit from the north-west. */
function columnFace(a: Art, { col, face, bark, splits, torn }: { col: Column; face: Path2D; bark: "lattice" | "ribs"; splits: number[]; torn: [number, number][] }): void {
  const { g } = a;
  const { c, R } = col;
  g.fillStyle = fill(a, "bark", 90);
  g.fill(face);
  g.save();
  g.clip(face);
  if (bark === "lattice") {
    lattice(a, face, { x0: c - R, y0: capY(col), x1: c + R, y1: footY(col) + R, period: 7 });
  } else {
    // Ribs run down the face, each the foot of a flute; seal scars sit between them in rows.
    const ribs = new Path2D();
    const scars = new Path2D();
    for (let k = 0; k < col.ribs; k++) {
      const ang = (k / col.ribs) * Math.PI * 2;
      if (Math.sin(ang) <= 0.05) continue;
      const r = columnRadius(col, ang);
      const x = c + Math.cos(ang) * r;
      ribs.moveTo(x, capY(col) + Math.sin(ang) * r);
      ribs.lineTo(x, footY(col) + Math.sin(ang) * r);
      const mid = ang + Math.PI / col.ribs;
      const rm = columnRadius(col, mid);
      const sx = c + Math.cos(mid) * rm;
      for (let y = capY(col) + 3; y < footY(col) - 1; y += 4) {
        const sy = y + Math.sin(mid) * rm;
        const w = Math.max(0.5, 1.1 * Math.sin(mid));
        scars.moveTo(sx + w, sy);
        scars.ellipse(sx, sy, w, 0.8, 0, 0, Math.PI * 2);
      }
    }
    g.strokeStyle = "rgba(18,11,6,0.7)";
    g.lineWidth = 1;
    g.stroke(ribs);
    g.fillStyle = "rgba(214,192,150,0.45)";
    g.fill(scars);
  }
  // Bark torn off the face shows the pale wood under it.
  for (const [from, width] of torn) {
    if (Math.sin(from + width / 2) < 0.2) continue;
    const patch = poly([
      ...outlineAt(col, { cx: c, cy: capY(col), k: 1.02, from, to: from + width }),
      ...outlineAt(col, { cx: c, cy: footY(col) - col.lift * (0.25 + 0.3 * r01(a, 50)), k: 1.02, from: from + width, to: from }),
    ]);
    sapwood(a, patch);
    g.strokeStyle = "rgba(20,12,6,0.85)";
    g.lineWidth = 1;
    g.stroke(patch);
  }
  // The cylinder's light: the west limb catches it, the east limb and the foot fall into shade.
  const grad = g.createLinearGradient(c - R, 0, c + R, 0);
  grad.addColorStop(0, "rgba(255,240,210,0.14)");
  grad.addColorStop(0.35, "rgba(0,0,0,0.25)");
  grad.addColorStop(1, "rgba(0,0,0,0.62)");
  g.fillStyle = grad;
  g.fill(face);
  const foot = g.createLinearGradient(0, capY(col) + R * 0.8, 0, footY(col) + R);
  foot.addColorStop(0, "rgba(0,0,0,0)");
  foot.addColorStop(1, "rgba(0,0,0,0.35)");
  g.fillStyle = foot;
  g.fill(face);
  // A split runs on down the face.
  for (const s of splits) {
    if (Math.sin(s) < 0.15) continue;
    const r = columnRadius(col, s);
    const x = c + Math.cos(s) * r;
    const y0 = capY(col) + Math.sin(s) * r;
    const y1 = footY(col) + Math.sin(s) * r - col.lift * 0.2;
    const gash = new Path2D();
    gash.moveTo(x - 2.2, y0);
    for (let k = 1; k <= 4; k++) gash.lineTo(x + (r01(a, 80 + k) - 0.5) * 2.5 - 1.2 * (1 - k / 4), y0 + ((y1 - y0) * k) / 4);
    gash.lineTo(x + 2.2, y0);
    gash.closePath();
    g.fillStyle = "rgba(8,5,3,0.95)";
    g.fill(gash);
  }
  g.restore();
}

/** Bared sapwood where bark has been torn away: pale, stringy, a little raw. */
function sapwood(a: Art, path: Path2D): void {
  const { g } = a;
  g.fillStyle = fill(a, "bark", 90);
  g.fill(path);
  g.fillStyle = "rgba(190,148,100,0.8)";
  g.fill(path);
}

/** The cut: the bark ring, pale wood with a darker heart, eccentric growth rings, the pith; splits and torn bark when damaged. */
function cutFace(a: Art, { col, cap, bark, splits, torn }: { col: Column; cap: Path2D; bark: "lattice" | "ribs"; splits: number[]; torn: [number, number][] }): void {
  const { g } = a;
  const { c, R } = col;
  const cy = capY(col);
  const barkK = bark === "lattice" ? 0.86 : 0.88;
  g.fillStyle = fill(a, "bark-dark", 45);
  g.fill(cap);
  const ring = poly(outlineAt(col, { cx: c, cy, k: barkK }), poly(outlineAt(col, { cx: c, cy, k: 1 })));
  g.save();
  g.clip(ring, "evenodd");
  if (bark === "lattice") {
    lattice(a, cap, { x0: c - R, y0: cy - R, x1: c + R, y1: cy + R, period: 5 });
  } else {
    const ticks = new Path2D();
    for (let k = 0; k < col.ribs; k++) {
      const ang = (k / col.ribs) * Math.PI * 2;
      const r = columnRadius(col, ang);
      ticks.moveTo(c + Math.cos(ang) * r * barkK, cy + Math.sin(ang) * r * barkK);
      ticks.lineTo(c + Math.cos(ang) * r, cy + Math.sin(ang) * r);
    }
    g.strokeStyle = "rgba(14,9,5,0.75)";
    g.lineWidth = 1.2;
    g.stroke(ticks);
  }
  g.restore();

  const wood = poly(outlineAt(col, { cx: c, cy, k: barkK }));
  g.fillStyle = fill(a, "bark-cap", 20);
  g.fill(wood);
  // A warm wash quiets the material's cracks so the rings carry the grain.
  g.fillStyle = "rgba(196,154,106,0.38)";
  g.fill(wood);
  g.save();
  g.clip(wood);
  // The pith sits a little off-centre; the rings widen from it toward the bark.
  const px = c + (r01(a, 5) - 0.5) * R * 0.16;
  const py = cy + (r01(a, 6) - 0.5) * R * 0.16;
  const heart = g.createRadialGradient(px, py, 0, px, py, R * barkK);
  heart.addColorStop(0, "rgba(120,64,26,0.55)");
  heart.addColorStop(0.5, "rgba(140,84,40,0.3)");
  heart.addColorStop(0.62, "rgba(0,0,0,0)");
  heart.addColorStop(0.9, "rgba(255,238,200,0.12)");
  heart.addColorStop(1, "rgba(60,36,16,0.35)");
  g.fillStyle = heart;
  g.fill(wood);
  const rings = new Path2D();
  const dark = new Path2D();
  let f = 0.07;
  for (let k = 0; f < 0.97; k++) {
    const pts = outlineAt(col, { cx: px + (c - px) * f, cy: py + (cy - py) * f, k: barkK * f });
    poly(pts, k % 4 === 3 ? dark : rings);
    f += 0.05 + 0.045 * r01(a, 400 + k);
  }
  g.strokeStyle = "rgba(92,56,26,0.42)";
  g.lineWidth = 0.8;
  g.stroke(rings);
  g.strokeStyle = "rgba(70,40,18,0.6)";
  g.lineWidth = 1.2;
  g.stroke(dark);
  // Fine checks where the wood dried, from the edge in.
  const checks = new Path2D();
  for (let k = 0; k < 4; k++) {
    const ang = r01(a, 450 + k) * Math.PI * 2;
    const r = columnRadius(col, ang) * barkK;
    const inTo = 0.55 + 0.3 * r01(a, 460 + k);
    checks.moveTo(c + Math.cos(ang) * r, cy + Math.sin(ang) * r);
    checks.lineTo(px + Math.cos(ang + 0.04) * r * (1 - inTo), py + Math.sin(ang + 0.04) * r * (1 - inTo));
  }
  g.strokeStyle = "rgba(60,34,14,0.55)";
  g.lineWidth = 0.9;
  g.stroke(checks);
  g.fillStyle = "rgba(60,32,12,0.9)";
  g.beginPath();
  g.ellipse(px, py, R * 0.035, R * 0.03, r01(a, 7) * 3, 0, Math.PI * 2);
  g.fill();
  g.restore();
  g.strokeStyle = "rgba(30,18,8,0.7)";
  g.lineWidth = 1;
  g.stroke(wood);

  // Torn bark: the ring gone in a ragged sector, the sapwood under it bared.
  for (const [from, width] of torn) {
    const inner = outlineAt(col, { cx: c, cy, k: barkK - 0.01, from: from + width, to: from });
    const outer = outlineAt(col, { cx: c, cy, k: 1.01, from, to: from + width }).map(([x, y], i): Point => {
      const bite = i === 0 ? 0 : (r01(a, 500 + i) - 0.3) * R * 0.05;
      return [x - ((x - c) / R) * bite, y - ((y - cy) / R) * bite];
    });
    const bare = poly([...outer, ...inner]);
    sapwood(a, bare);
    g.strokeStyle = "rgba(20,12,6,0.9)";
    g.lineWidth = 1;
    g.stroke(bare);
  }
  shade(a, cap, { x0: c - R, y0: cy - R, x1: c + R, y1: cy + R, lit: 0.18, dark: 0.3 });

  // A chunk broken out of the rim: a lower, splintered surface in shadow.
  if (a.damaged) {
    const at = r01(a, 90) * Math.PI * 2;
    const inner = outlineAt(col, { cx: c, cy, k: 0.7, from: at + 0.34, to: at - 0.34 }).map(([x, y], i): Point => {
      const jag = (r01(a, 540 + i) - 0.5) * R * 0.1;
      return [x + ((x - c) / R) * jag, y + ((y - cy) / R) * jag];
    });
    const chunk = poly([...outlineAt(col, { cx: c, cy, k: 1.01, from: at - 0.34, to: at + 0.34 }), ...inner]);
    g.fillStyle = fill(a, "bark-cap", 70);
    g.fill(chunk);
    g.fillStyle = "rgba(40,24,10,0.62)";
    g.fill(chunk);
    const grain = new Path2D();
    for (let k = 0; k < 6; k++) {
      const t = at - 0.28 + (0.56 * k) / 5;
      const r = columnRadius(col, t);
      grain.moveTo(c + Math.cos(t) * r * 0.76, cy + Math.sin(t) * r * 0.76);
      grain.lineTo(c + Math.cos(t) * r * (0.9 + 0.08 * r01(a, 550 + k)), cy + Math.sin(t) * r * (0.9 + 0.08 * r01(a, 550 + k)));
    }
    g.strokeStyle = "rgba(214,180,130,0.55)";
    g.lineWidth = 0.8;
    g.stroke(grain);
    g.strokeStyle = "rgba(10,6,3,0.9)";
    g.lineWidth = 1.2;
    g.stroke(chunk);
  }

  // Splits: dark wedges from near the pith out through the bark.
  for (const [k, s] of splits.entries()) {
    const r = columnRadius(col, s);
    const tip: Point = [px + Math.cos(s) * r * 0.12, py + Math.sin(s) * r * 0.12];
    const half = 0.1 + 0.05 * r01(a, 520 + k);
    const side1: Point[] = [];
    const side2: Point[] = [];
    for (let q = 1; q <= 5; q++) {
      const t = q / 5;
      const d = r * (0.12 + 0.93 * t);
      const jag = (r01(a, 530 + k * 10 + q) - 0.5) * 0.05;
      side1.push([px + Math.cos(s - half * t + jag) * d, py + Math.sin(s - half * t + jag) * d]);
      side2.push([px + Math.cos(s + half * t + jag) * d, py + Math.sin(s + half * t + jag) * d]);
    }
    const wedge = poly([tip, ...side1, ...side2.reverse()]);
    g.save();
    g.clip(cap);
    g.fillStyle = "rgba(8,5,3,0.96)";
    g.fill(wedge);
    g.strokeStyle = "rgba(120,78,40,0.8)";
    g.lineWidth = 0.8;
    g.stroke(wedge);
    g.restore();
  }
  g.strokeStyle = "rgba(14,9,5,0.9)";
  g.lineWidth = 1.2;
  g.stroke(cap);
}


// ── Horsetail stand ─────────────────────────────────────────────────────────────────────────────

const WHORL = { live: ["#2f4a17", "#4f7426", "#7fa447"], dry: ["#5e5226", "#8a7c3c", "#b3a25a"] } as const;

/**
 * A calamites thicket from above: a ragged mat of undergrowth, and on it a crowd of jointed stems,
 * each seen end-on as stacked needle whorls shrinking toward the tip. Short shadows, as soft cover.
 * Damaged: about half the stems snapped and lying, the rest yellowing.
 */
function horsetails(a: Art, side: number): void {
  const { g } = a;
  const c = side / 2;
  const mat = new Path2D();
  const n = 20;
  for (let k = 0; k < n; k++) {
    const ang = (k / n) * Math.PI * 2;
    // A squarish blob: the footprint's corners pulled in, the edge roughened.
    const box = c - 4;
    const r = Math.min(box / Math.abs(Math.cos(ang) || 1e-6), box / Math.abs(Math.sin(ang) || 1e-6)) * (0.9 - 0.08 * rnd(a.seed, k, 30));
    const x = c + Math.cos(ang) * Math.min(r, box * 1.18);
    const y = c + Math.sin(ang) * Math.min(r, box * 1.18);
    if (k === 0) mat.moveTo(x, y);
    else mat.lineTo(x, y);
  }
  mat.closePath();
  g.save();
  g.globalAlpha = 0.7;
  g.fillStyle = fill(a, a.damaged ? "litter" : "brush");
  g.fill(mat);
  g.restore();
  const stems: Point[] = [];
  const grid = Math.max(2, Math.round(side / 16));
  const step = (side - 14) / grid;
  for (let j = 0; j < grid; j++) {
    for (let i = 0; i < grid; i++) {
      stems.push([7 + (i + 0.2 + 0.6 * rnd(a.seed, i * 7 + j, 32)) * step, 7 + (j + 0.2 + 0.6 * rnd(a.seed, i * 7 + j, 34)) * step]);
    }
  }
  stems.sort((p, q) => p[1] - q[1]);
  // Every other stem snaps; the fallen ones lie across the standing ones.
  const snapped = (k: number) => a.damaged && (k + a.seed) % 2 === 0;
  stems.forEach(([x, y], k) => {
    if (!snapped(k)) whorl(a, { x, y, k, dry: a.damaged });
  });
  stems.forEach(([x, y], k) => {
    if (snapped(k)) fallenStem(a, { x, y, k, side });
  });
}

/** One stem from above: three whorls of fine needles, the lowest widest and darkest. */
function whorl(a: Art, { x, y, k, dry }: { x: number; y: number; k: number; dry: boolean }): void {
  const { g } = a;
  const colors = dry ? WHORL.dry : WHORL.live;
  const tip = new Path2D();
  tip.arc(x, y, 1.6, 0, Math.PI * 2);
  const base = new Path2D();
  base.arc(x, y, 7, 0, Math.PI * 2);
  castShadow(a, base, { dx: 2, dy: 3, blur: 3, alpha: 0.45 });
  const tiers = [
    { len: 10, n: 16, w: 0.9 },
    { len: 7, n: 13, w: 0.9 },
    { len: 4.2, n: 10, w: 0.8 },
  ];
  tiers.forEach((tier, t) => {
    const needles = new Path2D();
    const rot = rnd(a.seed, k * 3 + t, 43) * Math.PI;
    for (let q = 0; q < tier.n; q++) {
      const ang = rot + (q / tier.n) * Math.PI * 2;
      const l = tier.len * (0.8 + 0.2 * rnd(a.seed, k * 40 + t * 16 + q, 45));
      needles.moveTo(x + Math.cos(ang) * 1.2, y + Math.sin(ang) * 1.2);
      // A slight droop south-east: the needles arch down from the joint.
      needles.quadraticCurveTo(x + Math.cos(ang) * l * 0.6, y + Math.sin(ang) * l * 0.6 - 0.8, x + Math.cos(ang) * l + 0.5, y + Math.sin(ang) * l + 0.8);
    }
    g.lineCap = "round";
    g.strokeStyle = "rgba(10,18,6,0.45)";
    g.lineWidth = tier.w + 1;
    g.stroke(needles);
    g.strokeStyle = colors[t]!;
    g.lineWidth = tier.w;
    g.stroke(needles);
  });
  g.fillStyle = dry ? "#6a5a2a" : "#3c5a1c";
  g.fill(tip);
}

function fallenStem(a: Art, { x, y, k, side }: { x: number; y: number; k: number; side: number }): void {
  const { g } = a;
  const ang = rnd(a.seed, k, 47) * Math.PI * 2;
  const len = 10 + 6 * rnd(a.seed, k, 48);
  // Keep the whole stem inside the footprint.
  const ex = Math.min(side - 3, Math.max(3, x + Math.cos(ang) * len));
  const ey = Math.min(side - 3, Math.max(3, y + Math.sin(ang) * len));
  const stem = new Path2D();
  stem.moveTo(x, y);
  stem.lineTo(ex, ey);
  g.lineCap = "round";
  g.strokeStyle = "rgba(14,10,6,0.7)";
  g.lineWidth = 3.8;
  g.stroke(stem);
  g.strokeStyle = "#8a7f45";
  g.lineWidth = 2.4;
  g.stroke(stem);
  const joints = new Path2D();
  const l = Math.hypot(ex - x, ey - y);
  const nx = -(ey - y) / l;
  const ny = (ex - x) / l;
  for (let t = 4; t < l; t += 4.5) {
    const px = x + ((ex - x) * t) / l;
    const py = y + ((ey - y) * t) / l;
    joints.moveTo(px - nx * 1.6, py - ny * 1.6);
    joints.lineTo(px + nx * 1.6, py + ny * 1.6);
  }
  g.strokeStyle = "rgba(40,30,14,0.8)";
  g.lineWidth = 0.7;
  g.stroke(joints);
  // The snapped stump it fell from.
  g.fillStyle = "#6f6232";
  g.beginPath();
  g.arc(x, y, 2.2, 0, Math.PI * 2);
  g.fill();
}

// ── Fallen trunk ────────────────────────────────────────────────────────────────────────────────

/** Local frame → cell: the log is drawn along +x through the cell's middle, then turned to `dir`. */
const TURN: Record<TrunkPlace["dir"], number> = { e: 0, s: Math.PI / 2, w: Math.PI, n: -Math.PI / 2 };

/**
 * One section of a fallen trunk (orchestrator call 8): the log across its cell, joined flush to its
 * neighbours; the root plate on section 0, the broken crown on the last, and a splintered end
 * wherever a neighbouring section has been destroyed. The lattice's period divides a cell, so the
 * scars run on unbroken from section to section. Damaged: bark stripped and the log split.
 */
function trunkSection(a: Art, t: TrunkPlace): void {
  const { g } = a;
  const L = CELL;
  const hw = L * 0.3;
  const root = t.role === "root" || t.role === "single";
  const crown = t.role === "crown" || t.role === "single";
  const x0 = root ? L * 0.2 : t.brokenBefore ? L * 0.1 : 0;
  const x1 = crown ? L * 0.66 : t.brokenAfter ? L * 0.9 : L;
  const hw1 = crown ? hw * 0.75 : hw;
  const toCell = new DOMMatrix().translate(L / 2, L / 2).rotate((TURN[t.dir] * 180) / Math.PI).translate(-L / 2, -L / 2);
  const inCell = (local: Path2D): Path2D => {
    const p = new Path2D();
    p.addPath(local, toCell);
    return p;
  };
  const cy = L / 2;

  const localBody = new Path2D();
  localBody.moveTo(x0, cy - hw);
  localBody.lineTo(x1, cy - hw1);
  if (t.brokenAfter) jagged(localBody, { x: x1, y0: cy - hw1, y1: cy + hw1, salt: 60, seed: a.seed });
  else if (crown) localBody.quadraticCurveTo(x1 + hw1 * 0.9, cy, x1, cy + hw1);
  else localBody.lineTo(x1, cy + hw1);
  localBody.lineTo(x0, cy + hw);
  if (t.brokenBefore) jagged(localBody, { x: x0, y0: cy + hw, y1: cy - hw, salt: 70, seed: a.seed });
  localBody.closePath();
  const body = inCell(localBody);

  castShadow(a, body, { dx: 4, dy: 6, blur: 5, alpha: 0.7 });
  if (crown) brokenCrown(a, { x: x1, cy, hw: hw1, toCell });
  // The bark tiles once per cell, unoffset, so it runs on unbroken from section to section.
  g.fillStyle = pattern(g, a.m, { id: "bark", rot: (TURN[t.dir] * 180) / Math.PI + 90, scale: CELL / a.m.get("bark").repeat });
  g.fill(body);
  const scars = new Path2D();
  scars.addPath(localBody);
  g.save();
  g.setTransform(g.getTransform().multiply(toCell));
  lattice(a, scars, { x0: 0, y0: cy - hw, x1: L, y1: cy + hw, period: L / 5 });
  g.restore();
  if (a.damaged) {
    const strip = new Path2D();
    strip.ellipse(x0 + (x1 - x0) * (0.3 + 0.4 * r01(a, 51)), cy - hw * 0.25, (x1 - x0) * 0.2, hw * 0.4, 0, 0, Math.PI * 2);
    g.save();
    g.clip(body);
    g.fillStyle = fill(a, "bark-cap");
    g.fill(inCell(strip));
    g.restore();
  }
  // The cylinder's light, across the log whichever way it lies.
  const across = t.dir === "e" || t.dir === "w";
  const grad = across ? g.createLinearGradient(0, cy - hw, 0, cy + hw) : g.createLinearGradient(cy - hw, 0, cy + hw, 0);
  grad.addColorStop(0, "rgba(255,244,214,0.3)");
  grad.addColorStop(0.35, "rgba(0,0,0,0)");
  grad.addColorStop(1, "rgba(0,0,0,0.5)");
  g.fillStyle = grad;
  g.fill(body);
  // The outline, left open where the log runs on into a neighbour, so the sections read as one.
  const openBefore = !t.brokenBefore && !root;
  const openAfter = !t.brokenAfter && !crown;
  g.save();
  if (openBefore || openAfter) {
    const keep = new Path2D();
    keep.rect(-ASSET_MARGIN, -ASSET_MARGIN, L + 2 * ASSET_MARGIN, L + 2 * ASSET_MARGIN);
    const ends = new Path2D();
    if (openBefore) ends.rect(-ASSET_MARGIN, cy - hw + 1.5, ASSET_MARGIN + 1.5, 2 * hw - 3);
    if (openAfter) ends.rect(L - 1.5, cy - hw + 1.5, ASSET_MARGIN + 1.5, 2 * hw - 3);
    keep.addPath(inCell(ends));
    g.clip(keep, "evenodd");
  }
  g.strokeStyle = "rgba(16,10,6,0.85)";
  g.lineWidth = 1.2;
  g.stroke(body);
  g.restore();
  if (t.brokenBefore) woodEnd(a, inCell, { x: x0, cy, hw, salt: 70 });
  if (t.brokenAfter) woodEnd(a, inCell, { x: x1, cy, hw: hw1, salt: 60 });
  if (root) rootPlate(a, { x: x0, cy, inCell });
  if (a.damaged) {
    const localCrack = [x0 + 3, cy + hw * 0.2, x1 - 3, cy - hw * 0.1];
    const p0 = toCell.transformPoint({ x: localCrack[0]!, y: localCrack[1]! });
    const p1 = toCell.transformPoint({ x: localCrack[2]!, y: localCrack[3]! });
    crack(a, { from: [p0.x, p0.y], to: [p1.x, p1.y], salt: 80 });
  }
}

/** A zigzag broken edge from `(x, y0)` to `(x, y1)`, continuing `path`. */
function jagged(path: Path2D, { x, y0, y1, salt, seed }: { x: number; y0: number; y1: number; salt: number; seed: number }): void {
  const teeth = 5;
  for (let k = 1; k <= teeth; k++) {
    const y = y0 + ((y1 - y0) * k) / teeth;
    const dx = k === teeth ? 0 : (k % 2 === 0 ? -1 : 1) * (1.5 + 2.5 * rnd(seed, salt + k, 61));
    path.lineTo(x + dx, y);
  }
}

/** The sawn-looking pale wood of a broken end, with a few splinters. */
function woodEnd(a: Art, inCell: (p: Path2D) => Path2D, { x, cy, hw, salt }: { x: number; cy: number; hw: number; salt: number }): void {
  const { g } = a;
  const face = new Path2D();
  face.ellipse(x, cy, 2.6, hw * 0.9, 0, 0, Math.PI * 2);
  g.fillStyle = fill(a, "bark-cap");
  g.fill(inCell(face));
  const sp = new Path2D();
  for (let k = 0; k < 4; k++) {
    const y = cy - hw * 0.7 + (hw * 1.4 * k) / 3;
    const dir = x < CELL / 2 ? -1 : 1;
    sp.moveTo(x, y);
    sp.lineTo(x + dir * (2 + 3 * rnd(a.seed, salt + k, 63)), y + (rnd(a.seed, salt + k, 64) - 0.5) * 3);
  }
  g.lineCap = "round";
  g.strokeStyle = "rgba(232,212,170,0.9)";
  g.lineWidth = 1;
  g.stroke(inCell(sp));
}

/** The upended root plate at the log's base: a clod of earth with roots splayed through it. */
function rootPlate(a: Art, { x, cy, inCell }: { x: number; cy: number; inCell: (p: Path2D) => Path2D }): void {
  const { g } = a;
  const plate = new Path2D();
  plate.ellipse(x - 1, cy, CELL * 0.14, CELL * 0.42, 0, 0, Math.PI * 2);
  const inPlate = inCell(plate);
  castShadow(a, inPlate, { dx: 4, dy: 6, blur: 5, alpha: 0.6 });
  g.fillStyle = fill(a, "mine-soil");
  g.fill(inPlate);
  const roots = new Path2D();
  for (let k = 0; k < 7; k++) {
    const y = cy - CELL * 0.36 + (CELL * 0.72 * k) / 6;
    roots.moveTo(x + 1, cy + (y - cy) * 0.3);
    roots.quadraticCurveTo(x - 2, y, x - CELL * 0.12 + rnd(a.seed, k, 65) * 3, y + (rnd(a.seed, k, 66) - 0.5) * 4);
  }
  g.lineCap = "round";
  g.strokeStyle = "rgba(14,10,6,0.8)";
  g.lineWidth = 2.4;
  g.stroke(inCell(roots));
  g.strokeStyle = fill(a, "bark-dark");
  g.lineWidth = 1.4;
  g.stroke(inCell(roots));
  shade(a, inPlate, { x0: 0, y0: 0, x1: CELL, y1: CELL, lit: 0.2, dark: 0.4 });
  g.strokeStyle = "rgba(16,10,6,0.8)";
  g.lineWidth = 1;
  g.stroke(inPlate);
}

/** The crown end: forked limb stubs past the taper and a dried tuft among them. */
function brokenCrown(a: Art, { x, cy, hw, toCell }: { x: number; cy: number; hw: number; toCell: DOMMatrix }): void {
  const reach = CELL - 3 - x;
  const stubs = new Path2D();
  for (const s of [-1, 1]) {
    const fx = x + reach * 0.45;
    const fy = cy + s * hw * 0.45;
    stubs.moveTo(x - 2, cy + s * hw * 0.3);
    stubs.lineTo(fx, fy);
    stubs.lineTo(x + reach, fy + s * hw * 0.55);
    stubs.moveTo(fx, fy);
    stubs.lineTo(x + reach * 0.9, fy - s * hw * 0.25);
  }
  const path = new Path2D();
  path.addPath(stubs, toCell);
  limb(a, path, 2.4);
  const at = toCell.transformPoint({ x: x + reach * 0.62, y: cy });
  tuft(a, { x: at.x, y: at.y, r: Math.min(reach * 0.55, 8), n: 11, salt: 700, dry: true });
}

// ── Hauler wreck ────────────────────────────────────────────────────────────────────────────────

/** Industrial paint over the bare hull metal: hauler yellow, or a rust-orange variant. */
const HAULER_PAINT = ["#c49a2c", "#b8642c"] as const;

/** The cargo container's paint, as the cargo-stacks tile's containers wear it. */
const CONTAINER_PAINT = ["#7a3b2a", "#2f5a6e", "#6b6a3a"] as const;

/**
 * A wrecked industrial cargo hauler seen from above: a tracked chassis with the cab at one end and
 * the cargo bed behind it, lying a little askew in its footprint on a scorched patch of ground.
 * Every band is already a wreck: plating torn back over an exposed frame, scorch, spilled cargo.
 * Damaged: the tear runs across the bed, the windscreen is gone and more cargo is thrown out.
 * Destroyed (a name-recognised wreck still placed at 0 HP): flattened, the cab and bed broken apart
 * and low to the ground, a thrown track, debris everywhere.
 */
function haulerWreck(a: Art, side: number, band: AssetBand): void {
  const { g } = a;
  const u = side / CELL;
  const c = side / 2;
  const flat = band === "destroyed";
  const hurt = band !== "intact";
  // A little askew, either way round; at up to 6° the tracks stay inside the footprint.
  const deg = (r01(a, 1) - 0.5) * 12 + (r01(a, 2) < 0.5 ? 0 : 180);
  const L = side * 0.86;
  const W = side * 0.54;
  const toFoot = new DOMMatrix().translate(c, c).rotate(deg);
  const T = (local: Path2D, m: DOMMatrix): Path2D => {
    const p = new Path2D();
    p.addPath(local, m);
    return p;
  };
  const paint = HAULER_PAINT[Math.floor(r01(a, 3) * HAULER_PAINT.length)]!;
  const load = CONTAINER_PAINT[Math.floor(r01(a, 4) * CONTAINER_PAINT.length)]!;

  scorch(a, { side, r: side * (flat ? 0.56 : hurt ? 0.5 : 0.42), n: flat ? 4 : hurt ? 3 : 2 });

  // The cab piece and the bed piece: one hull, or two pulled apart and askew when flattened.
  const cabLen = L * 0.28;
  const x0 = -L / 2;
  const xCab = L / 2 - cabLen;
  const cabM = flat ? toFoot.translate(1.6 * u, -1.4 * u).rotate(9) : toFoot;
  const bedM = flat ? toFoot.translate(-1.2 * u, 1 * u).rotate(-4) : toFoot;
  const bedEnd = flat ? xCab - 1.2 * u : xCab;
  const cabStart = flat ? xCab + 1.2 * u : xCab;

  // Tracks along both flanks, half out from under the hull; a flattened wreck has thrown one.
  const trackW = W * 0.24;
  const trackLocal = (y: number): Path2D => {
    const p = new Path2D();
    p.roundRect(x0 + L * 0.03, y, L * 0.9, trackW, trackW * 0.4);
    return p;
  };
  const nearY = -W / 2 - trackW * 0.5;
  const farY = W / 2 - trackW * 0.5;
  const tracks = [T(trackLocal(nearY), bedM), T(trackLocal(farY), flat ? toFoot.translate(1 * u, W * 0.2).rotate(-8) : bedM)];

  const bedLocal = new Path2D();
  bedLocal.moveTo(x0, -W / 2);
  bedLocal.lineTo(bedEnd, -W / 2);
  if (flat) jagged(bedLocal, { x: bedEnd, y0: -W / 2, y1: W / 2, salt: 10, seed: a.seed });
  else bedLocal.lineTo(bedEnd, W / 2);
  bedLocal.lineTo(x0, W / 2);
  bedLocal.closePath();
  const bevel = cabLen * 0.34;
  const cabLocal = new Path2D();
  if (flat) {
    cabLocal.moveTo(cabStart, W / 2);
    jagged(cabLocal, { x: cabStart, y0: W / 2, y1: -W / 2, salt: 20, seed: a.seed });
  } else {
    cabLocal.moveTo(cabStart, -W / 2);
  }
  cabLocal.lineTo(L / 2 - bevel, -W / 2);
  cabLocal.lineTo(L / 2, -W / 2 + bevel);
  cabLocal.lineTo(L / 2, W / 2 - bevel);
  cabLocal.lineTo(L / 2 - bevel, W / 2);
  cabLocal.lineTo(cabStart, W / 2);
  cabLocal.closePath();
  const bed = T(bedLocal, bedM);
  const cab = T(cabLocal, cabM);
  const hull = new Path2D();
  hull.addPath(bed);
  hull.addPath(cab);
  const silhouette = new Path2D();
  silhouette.addPath(hull);
  for (const t of tracks) silhouette.addPath(t);

  // A flattened wreck sits low, so its shadow is short.
  castShadow(a, silhouette, flat ? { dx: 1.5 * u, dy: 2 * u, blur: 2 * u, alpha: 0.6 } : { dx: 3 * u, dy: 4 * u, blur: 3 * u, alpha: 0.75 });

  for (const t of tracks) {
    g.fillStyle = fill(a, "metal-dark", deg);
    g.fill(t);
  }
  // Track shoes across each tread.
  const shoes = new Path2D();
  for (let x = x0 + L * 0.05; x < x0 + L * 0.92; x += 2.4 * u) {
    for (const y of [nearY, farY]) {
      shoes.moveTo(x, y + 0.8);
      shoes.lineTo(x, y + trackW - 0.8);
    }
  }
  g.strokeStyle = "rgba(160,160,150,0.4)";
  g.lineWidth = 0.7;
  g.stroke(T(shoes, bedM));
  for (const t of tracks) {
    g.strokeStyle = "rgba(8,8,10,0.92)";
    g.lineWidth = 1;
    g.stroke(t);
  }

  // The hull: bare metal under the hauler's paint.
  g.fillStyle = fill(a, "metal-bulk", deg);
  g.fill(hull);
  g.save();
  g.globalAlpha = flat ? 0.5 : 0.72;
  g.fillStyle = paint;
  g.fill(hull);
  g.restore();

  // The cargo container on the bed, corrugated across its width, in its own paint.
  const rim = 1.4 * u;
  const boxLocal = new Path2D();
  boxLocal.rect(x0 + rim, -W / 2 + rim, bedEnd - x0 - 2 * rim, W - 2 * rim);
  const box = T(boxLocal, bedM);
  g.fillStyle = fill(a, "metal-bulk", deg + 90);
  g.fill(box);
  g.save();
  g.globalAlpha = flat ? 0.45 : 0.7;
  g.fillStyle = load;
  g.fill(box);
  g.restore();
  const ribs = new Path2D();
  for (let x = x0 + rim + 1.6 * u; x < bedEnd - rim - 0.5; x += 1.9 * u) {
    ribs.moveTo(x, -W / 2 + rim + 0.6);
    ribs.lineTo(x, W / 2 - rim - 0.6);
  }
  g.save();
  g.clip(box);
  g.strokeStyle = "rgba(10,10,12,0.35)";
  g.lineWidth = 0.7 * u;
  g.stroke(T(ribs, bedM));
  g.restore();
  g.strokeStyle = "rgba(12,10,8,0.8)";
  g.lineWidth = 0.9;
  g.stroke(box);

  // The container's roof torn open over the frame beneath; across most of it when damaged.
  const boxLen = bedEnd - x0 - 2 * rim;
  const tearLen = boxLen * (flat ? 0.9 : hurt ? 0.78 : 0.5);
  const tearX = x0 + rim + (boxLen - tearLen) * (hurt ? 0.5 : r01(a, 5));
  tornPlating(a, { m: bedM, x: tearX, len: tearLen, W: W - 2 * rim, side: r01(a, 6) < 0.5 ? -1 : 1, deep: flat ? 1 : hurt ? 0.85 : 0.6 });

  // The cab: a roof panel, the windscreen at the front, a hazard stripe along its back edge.
  const roofLocal = new Path2D();
  roofLocal.rect(cabStart + 2.2 * u, -W / 2 + 1.8 * u, cabLen - bevel - 2.6 * u, W - 3.6 * u);
  g.strokeStyle = "rgba(20,18,14,0.6)";
  g.lineWidth = 0.8;
  g.stroke(T(roofLocal, cabM));
  const glassLocal = new Path2D();
  glassLocal.moveTo(L / 2 - bevel * 1.05, -W / 2 + bevel * 0.55);
  glassLocal.lineTo(L / 2 - bevel * 0.35, -W / 2 + bevel * 1.05);
  glassLocal.lineTo(L / 2 - bevel * 0.35, W / 2 - bevel * 1.05);
  glassLocal.lineTo(L / 2 - bevel * 1.05, W / 2 - bevel * 0.55);
  glassLocal.closePath();
  const glass = T(glassLocal, cabM);
  g.fillStyle = hurt ? "rgba(8,8,10,0.97)" : "rgba(38,58,68,0.97)";
  g.fill(glass);
  if (!hurt) {
    const glint = new Path2D();
    glint.moveTo(L / 2 - bevel * 0.9, -W / 2 + bevel * 0.9);
    glint.lineTo(L / 2 - bevel * 0.55, -W * 0.08);
    g.strokeStyle = "rgba(200,225,235,0.6)";
    g.lineWidth = 0.8;
    g.stroke(T(glint, cabM));
  }
  const stripeLocal = new Path2D();
  stripeLocal.rect(cabStart, -W / 2, 2 * u, W);
  const stripe = T(stripeLocal, cabM);
  g.save();
  g.clip(stripe);
  g.clip(cab);
  g.fillStyle = "#16140f";
  g.fill(stripe);
  const chev = new Path2D();
  for (let y = -W / 2 - 2 * u; y < W / 2 + 2 * u; y += 2.6 * u) {
    chev.moveTo(cabStart - 1 * u, y);
    chev.lineTo(cabStart + 3 * u, y + 1.8 * u);
  }
  g.strokeStyle = "#e0b83a";
  g.lineWidth = 1 * u;
  g.stroke(T(chev, cabM));
  g.restore();

  // Scorch across the hull, heavier the worse it is, then the light over everything.
  g.save();
  g.clip(hull);
  const burns = flat ? 4 : hurt ? 3 : 1;
  for (let k = 0; k < burns; k++) {
    const b = toFoot.transformPoint({ x: (r01(a, 30 + k) - 0.5) * L * 0.8, y: (r01(a, 40 + k) - 0.5) * W * 0.6 });
    const br = side * (0.16 + 0.12 * r01(a, 50 + k));
    const grad = g.createRadialGradient(b.x, b.y, 0, b.x, b.y, br);
    grad.addColorStop(0, "rgba(8,6,4,0.9)");
    grad.addColorStop(0.5, "rgba(20,14,8,0.6)");
    grad.addColorStop(1, "rgba(0,0,0,0)");
    g.fillStyle = grad;
    g.fillRect(b.x - br, b.y - br, br * 2, br * 2);
  }
  g.restore();
  shade(a, hull, { x0: 0, y0: 0, x1: side, y1: side, lit: 0.26, dark: 0.45 });
  if (hurt) crack(a, { from: pointIn(cabM, cabStart + 2.5 * u, -W * 0.32), to: pointIn(cabM, L / 2 - bevel, W * 0.28), salt: 60 });
  g.strokeStyle = "rgba(10,9,8,0.92)";
  g.lineWidth = 1.3;
  g.stroke(hull);
  // Cargo thrown out past the torn container, over the hull edge.
  cargo(a, { side, toFoot: bedM, x: tearX + tearLen * 0.5, y: 0, n: flat ? 5 : hurt ? 3 : 1, spread: flat ? L * 0.45 : L * 0.3 });
  if (flat) fragments(a, { side, n: 10 });
}

function pointIn(m: DOMMatrix, x: number, y: number): Point {
  const p = m.transformPoint({ x, y });
  return [p.x, p.y];
}

/** A burnt patch of ground under the wreck: overlapping soft dark blots, inside the footprint. */
function scorch(a: Art, { side, r, n }: { side: number; r: number; n: number }): void {
  const { g } = a;
  g.save();
  const box = new Path2D();
  box.rect(0, 0, side, side);
  g.clip(box);
  for (let k = 0; k < n; k++) {
    const x = side / 2 + (r01(a, 300 + k) - 0.5) * side * 0.3;
    const y = side / 2 + (r01(a, 310 + k) - 0.5) * side * 0.3;
    const rr = r * (0.75 + 0.35 * r01(a, 320 + k));
    const grad = g.createRadialGradient(x, y, rr * 0.15, x, y, rr);
    grad.addColorStop(0, "rgba(14,10,8,0.6)");
    grad.addColorStop(0.6, "rgba(28,20,14,0.32)");
    grad.addColorStop(1, "rgba(0,0,0,0)");
    g.fillStyle = grad;
    g.fillRect(x - rr, y - rr, rr * 2, rr * 2);
  }
  g.restore();
}

/**
 * Plating torn back along one flank of the bed: the hole shows the dark hull interior crossed by
 * the lit girders of the frame, and the torn edge is curled up and catches the light.
 */
function tornPlating(a: Art, { m, x, len, W, side, deep }: { m: DOMMatrix; x: number; len: number; W: number; side: -1 | 1; deep: number }): void {
  const { g } = a;
  const edge = (side * W) / 2;
  const reach = side * W * deep;
  const local = new Path2D();
  local.moveTo(x, edge);
  const teeth = 7;
  const inner: Point[] = [];
  for (let k = 1; k < teeth; k++) {
    const t = k / teeth;
    const bite = Math.sin(t * Math.PI) * (0.55 + 0.45 * r01(a, 200 + k));
    inner.push([x + len * t + (r01(a, 210 + k) - 0.5) * 2, edge - reach * bite]);
  }
  for (const p of inner) local.lineTo(...p);
  local.lineTo(x + len, edge);
  local.closePath();
  const hole = new Path2D();
  hole.addPath(local, m);
  g.fillStyle = "rgba(12,11,10,0.97)";
  g.fill(hole);
  // The frame under the plating: a longitudinal member and a few cross girders.
  const frame = new Path2D();
  frame.moveTo(x - 1, edge - reach * 0.4);
  frame.lineTo(x + len + 1, edge - reach * 0.4);
  for (let fx = x + len * 0.22; fx < x + len; fx += Math.max(5, len * 0.3)) {
    frame.moveTo(fx, edge + side);
    frame.lineTo(fx, edge - reach);
  }
  const frameP = new Path2D();
  frameP.addPath(frame, m);
  g.save();
  g.clip(hole);
  g.strokeStyle = "rgba(4,4,4,0.9)";
  g.lineWidth = 2.2;
  g.stroke(frameP);
  g.strokeStyle = fill(a, "metal-dark");
  g.lineWidth = 1.4;
  g.stroke(frameP);
  g.strokeStyle = "rgba(210,205,190,0.18)";
  g.lineWidth = 0.5;
  g.stroke(frameP);
  g.restore();
  // The curled lip of the torn plate.
  const lip = new Path2D();
  lip.moveTo(x, edge);
  for (const p of inner) lip.lineTo(...p);
  lip.lineTo(x + len, edge);
  const lipP = new Path2D();
  lipP.addPath(lip, m);
  g.lineJoin = "round";
  g.strokeStyle = "rgba(8,8,8,0.9)";
  g.lineWidth = 1.8;
  g.stroke(lipP);
  g.strokeStyle = "rgba(226,214,186,0.75)";
  g.lineWidth = 0.7;
  g.stroke(lipP);
}

/**
 * Cargo thrown out of the torn container: supply crates and drums scattered about `x, y` in the
 * frame `toFoot`, as far as `spread`, kept inside the footprint.
 */
function cargo(a: Art, { side, toFoot, x, y, n, spread }: { side: number; toFoot: DOMMatrix; x: number; y: number; n: number; spread: number }): void {
  const { g } = a;
  const inside = (v: number, r: number) => Math.min(side - r - 1, Math.max(r + 1, v));
  for (let k = 0; k < n; k++) {
    const ang = r01(a, 500 + k) * Math.PI * 2;
    const d = spread * (0.3 + 0.7 * r01(a, 510 + k));
    const p = toFoot.transformPoint({ x: x + Math.cos(ang) * d, y: y + Math.sin(ang) * d });
    const drum = r01(a, 520 + k) < 0.4;
    const s = side * (drum ? 0.07 : 0.08) * (0.85 + 0.3 * r01(a, 530 + k));
    const cx = inside(p.x, s * 1.3);
    const cy = inside(p.y, s * 1.3);
    const path = new Path2D();
    if (drum) {
      path.arc(cx, cy, s, 0, Math.PI * 2);
    } else {
      const rot = r01(a, 540 + k) * Math.PI;
      poly([0, 1, 2, 3].map((q): Point => [cx + Math.cos(rot + (q * Math.PI) / 2) * s * 1.25, cy + Math.sin(rot + (q * Math.PI) / 2) * s * 1.25]), path);
    }
    castShadow(a, path, { dx: 1.2, dy: 1.6, blur: 1.2, alpha: 0.65 });
    g.fillStyle = drum ? fill(a, "metal-bulk") : fill(a, "litter", 90);
    g.fill(path);
    g.save();
    g.globalAlpha = 0.65;
    g.fillStyle = drum ? CONTAINER_PAINT[k % CONTAINER_PAINT.length]! : "#9a7640";
    g.fill(path);
    g.restore();
    shade(a, path, { x0: cx - s, y0: cy - s, x1: cx + s, y1: cy + s, lit: 0.3, dark: 0.45 });
    g.strokeStyle = "rgba(12,10,8,0.9)";
    g.lineWidth = 0.8;
    g.stroke(path);
  }
}

/** Twisted plate fragments scattered across a flattened wreck's footprint. */
function fragments(a: Art, { side, n }: { side: number; n: number }): void {
  const { g } = a;
  const bits = new Path2D();
  for (let k = 0; k < n; k++) {
    const x = 3 + (side - 6) * r01(a, 600 + k);
    const y = 3 + (side - 6) * r01(a, 610 + k);
    const s = 1.2 + 1.8 * r01(a, 620 + k);
    const rot = r01(a, 630 + k) * Math.PI;
    poly([0, 1, 2].map((q): Point => [x + Math.cos(rot + q * 2.1) * s, y + Math.sin(rot + q * 2.1) * s * 0.7]), bits);
  }
  castShadow(a, bits, { dx: 0.8, dy: 1, blur: 0.8, alpha: 0.6 });
  g.fillStyle = fill(a, "metal-bulk");
  g.fill(bits);
  g.strokeStyle = "rgba(10,9,8,0.9)";
  g.lineWidth = 0.6;
  g.stroke(bits);
}
